// Read/write helpers for pop_market_sellers + related collections.
// All writes are gated through Cloud Functions for spam protection;
// reads happen directly from Firestore (public-by-default).

import {
  doc,
  getDoc,
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { SEGMENT_KEYS } from "./constants";
import { sellersCollectionName } from "./mode";

// Live collection for all writes (callables enforce uniqueness across this
// collection only). Reads use COL_READ which can flip to the seed
// collection when demo mode is on.
const COL = "pop_market_sellers";
function colRead() { return sellersCollectionName(); }

// ── Reads ────────────────────────────────────────────────────────────────────

export async function getSellerByHandle(handle) {
  if (!handle) return null;
  const snap = await getDoc(doc(db, colRead(), handle.toLowerCase()));
  return snap.exists() ? { handle: snap.id, ...snap.data() } : null;
}

export function watchSellerByHandle(handle, onChange) {
  if (!handle) return () => {};
  return onSnapshot(doc(db, colRead(), handle.toLowerCase()), (snap) => {
    onChange(snap.exists() ? { handle: snap.id, ...snap.data() } : null);
  });
}

// Directory query — `opts` keys are all optional.
//   game:      "BGMI" | "PUBGM"
//   region:    "Global" | "India" | …
//   segment:   "mix" | "lumen" | "helicopter" | "premium" — must offer it
//   minReserve "1M" | "10M" | "50M"
//   tier:      "1".."6"
//   sort:      "newest" | "cheapestMix" | "topRated" | "mostReviewed" | "highestStock"
//   pageSize:  number, default 24
//   cursor:    DocumentSnapshot (the last doc from the prior page)
//
// Strategy: do a single-field server query (status==active) + orderBy +
// limit, then filter the rest client-side. This avoids the composite-
// index dependency entirely for now — works while indexes are still
// being built and remains correct as the dataset grows to thousands.
// When the collection gets >10k docs we'll revisit and add server-side
// filters back.
export async function querySellers(opts = {}) {
  const {
    game,
    region,
    segment,
    minReserve,
    tier,
    sort = "newest",
    pageSize = 24,
    cursor = null,
  } = opts;

  // Server side: pure orderBy, no WHERE filters. Single-field auto-index
  // covers it. All filters (status, paused, game, region, tier, segment,
  // reserve) happen client-side on an over-fetched page. Correct for
  // collections up to ~10k docs; we'll switch back to indexed server-side
  // queries when the real collection crosses that.
  const orderClause = sortOrder(sort, segment);
  const clauses = [
    ...orderClause,
    limit(Math.max(pageSize * 4, 60)),
  ];
  if (cursor) clauses.splice(clauses.length - 1, 0, startAfter(cursor));

  const snap = await getDocs(query(collection(db, colRead()), ...clauses));
  const raw = snap.docs.map((d) => ({ handle: d.id, ...d.data() }));

  // Client-side filters
  const reserveTiers = minReserve && minReserve !== "none"
      ? reserveTiersAtOrAbove(minReserve) : null;
  const filtered = raw.filter((s) => {
    if (s.status !== "active") return false;
    if (s.paused) return false;
    if (game && s.game !== game) return false;
    if (region && s.region !== region) return false;
    if (tier && s.tier !== tier) return false;
    if (reserveTiers && !reserveTiers.includes(s.reserveProofTier)) return false;
    if (segment && SEGMENT_KEYS.includes(segment)) {
      const v = Number(s.stock?.[segment]) || 0;
      if (v <= 0) return false;
    }
    return true;
  }).slice(0, pageSize);

  return {
    sellers: filtered,
    nextCursor: snap.docs[snap.docs.length - 1] || null,
    done: raw.length < clauses[clauses.length - 1].limit,
  };
}

function reserveTiersAtOrAbove(min) {
  // OR-semantics on "minReserve" filter: 1M means anything ≥1M counts.
  if (min === "1M")  return ["1M", "10M", "50M"];
  if (min === "10M") return ["10M", "50M"];
  if (min === "50M") return ["50M"];
  return ["none", "1M", "10M", "50M"];
}

function sortOrder(sort, segment) {
  switch (sort) {
    case "cheapestMix":
      return [orderBy("rates.mix.base", "asc")];
    case "topRated":
      return [orderBy("avgRating", "desc"), orderBy("reviewCount", "desc")];
    case "mostReviewed":
      return [orderBy("reviewCount", "desc")];
    case "highestStock":
      if (segment) return [orderBy(`stock.${segment}`, "desc")];
      return [orderBy("stock.mix", "desc")];
    case "newest":
    default:
      return [orderBy("createdAt", "desc")];
  }
}

// ── Writes (all via callables to keep spam check server-side) ─────────────────

// Idempotent — checks fingerprint + identity + game-account-claim + crosspals.
// Returns { ok, handle, trustScore } or throws an HttpsError the caller
// surfaces as inline form errors.
export async function submitListing(payload) {
  return httpsCallable(functions, "popMarketSubmitListing")(payload)
    .then((r) => r.data);
}

export async function updateOwnSeller(handle, partial) {
  // Velocity-controlled on server — same callable as submit, payload tagged.
  return httpsCallable(functions, "popMarketUpdateListing")({ handle, partial })
    .then((r) => r.data);
}

// Pause/resume is a quick path that doesn't trigger anti-spam re-eval.
export async function setPaused(handle, paused) {
  await updateDoc(doc(db, COL, handle.toLowerCase()), {
    paused: !!paused,
    updatedAt: serverTimestamp(),
  });
}

export async function quickStockUpdate(handle, stockPartial) {
  // Frequent path — sellers refresh stock multiple times a day. We allow
  // direct write but security rules enforce ownership + numeric ranges.
  const ref = doc(db, COL, handle.toLowerCase());
  await updateDoc(ref, {
    ...Object.fromEntries(
        Object.entries(stockPartial).map(([k, v]) => [`stock.${k}`, Number(v) || 0]),
    ),
    "stock.lastUpdatedAt": serverTimestamp(),
    lastActiveAt: serverTimestamp(),
  });
}

// ── Contact reveal ──────────────────────────────────────────────────────────
// Contact values live in pop_market_seller_contacts (server-only). Direct
// Firestore reads from a client ALWAYS fail by rule. The callable below
// returns the value if the viewer is signed-in AND the target is not a
// seed/demo profile. Telemetry (click count + log) is written server-side.
//
//   await revealContact("rapidgorilla1", "whatsapp", "seller")
//     → { ok: true, channel: "whatsapp", value: "+91…" }
//   On any failure → throws an HttpsError the caller surfaces inline.
export async function revealContact(handle, channel, role = "seller") {
  return httpsCallable(functions, "popMarketRevealContact")({
    handle, channel, role,
  }).then((r) => r.data);
}
